/**
 * Blob Storage Service
 * Handles file upload, download, list, and delete operations for Provider and RunPlutus buckets
 */

import {
    DeleteResponse,
    FileInfo,
    ParsedFileInfo,
    ProviderFileListResponse,
    RunPlutusFileListResponse,
    UploadResponse,
} from "../types/blob";
import { formatFileSize } from "../utils/numberUtils";
import configService from "./configService";
import httpService from "./httpService";

/** Image MIME types that are previewable */
const IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

/** PDF MIME type */
const PDF_TYPE = "application/pdf";

class BlobService {
  private readonly baseUrl = "/v2/blobs";

  // ============================================
  // Provider Bucket Operations
  // ============================================

  /**
   * List files in a provider bucket folder
   */
  async listProviderFiles(
    providerId: string,
    subfolder?: string
  ): Promise<ProviderFileListResponse> {
    const queryParams = new URLSearchParams();
    if (subfolder) {
      queryParams.set("subfolder", subfolder);
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.baseUrl}/provider/${providerId}?${queryString}`
      : `${this.baseUrl}/provider/${providerId}`;

    const response = await httpService.get<ProviderFileListResponse>(url);
    return response.data;
  }

  /**
   * Upload a file to provider bucket
   */
  async uploadProviderFile(
    providerId: string,
    fileName: string,
    file: File,
    subfolder?: string,
    overwrite: boolean = false
  ): Promise<UploadResponse> {
    const queryParams = new URLSearchParams();
    if (subfolder) {
      queryParams.set("subfolder", subfolder);
    }
    if (overwrite) {
      queryParams.set("overwrite", "true");
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.baseUrl}/provider/${providerId}/${encodeURIComponent(fileName)}?${queryString}`
      : `${this.baseUrl}/provider/${providerId}/${encodeURIComponent(fileName)}`;

    const response = await httpService.post<UploadResponse>(url, file, {
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
    });
    return response.data;
  }

  /**
   * Delete a file from provider bucket
   */
  async deleteProviderFile(
    providerId: string,
    fileName: string,
    subfolder?: string
  ): Promise<DeleteResponse> {
    const queryParams = new URLSearchParams();
    if (subfolder) {
      queryParams.set("subfolder", subfolder);
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.baseUrl}/provider/${providerId}/${encodeURIComponent(fileName)}?${queryString}`
      : `${this.baseUrl}/provider/${providerId}/${encodeURIComponent(fileName)}`;

    const response = await httpService.delete<DeleteResponse>(url);
    return response.data;
  }

  /**
   * Download a file from provider bucket as Blob
   */
  async downloadProviderFile(
    providerId: string,
    fileName: string,
    subfolder?: string
  ): Promise<Blob> {
    const url = this.getProviderFileUrl(providerId, fileName, subfolder);
    return this.fetchAsBlob(url);
  }

  /**
   * Get the API URL for a provider file (for direct fetching or preview)
   */
  getProviderFileUrl(
    providerId: string,
    fileName: string,
    subfolder?: string
  ): string {
    const queryParams = new URLSearchParams();
    if (subfolder) {
      queryParams.set("subfolder", subfolder);
    }

    const queryString = queryParams.toString();
    const path = queryString
      ? `${this.baseUrl}/provider/${providerId}/${encodeURIComponent(fileName)}?${queryString}`
      : `${this.baseUrl}/provider/${providerId}/${encodeURIComponent(fileName)}`;

    return `${configService.getApiBaseUrl()}${path}`;
  }

  // ============================================
  // RunPlutus Bucket Operations
  // ============================================

  /**
   * List files in a RunPlutus bucket folder
   */
  async listRunPlutusFiles(folder: string): Promise<RunPlutusFileListResponse> {
    const url = `${this.baseUrl}/runplutus/${encodeURIComponent(folder)}`;
    const response = await httpService.get<RunPlutusFileListResponse>(url);
    return response.data;
  }

  /**
   * Upload a file to RunPlutus bucket
   */
  async uploadRunPlutusFile(
    folder: string,
    fileName: string,
    file: File,
    overwrite: boolean = false
  ): Promise<UploadResponse> {
    const queryParams = new URLSearchParams();
    if (overwrite) {
      queryParams.set("overwrite", "true");
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.baseUrl}/runplutus/${encodeURIComponent(folder)}/${encodeURIComponent(fileName)}?${queryString}`
      : `${this.baseUrl}/runplutus/${encodeURIComponent(folder)}/${encodeURIComponent(fileName)}`;

    const response = await httpService.post<UploadResponse>(url, file, {
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
    });
    return response.data;
  }

  /**
   * Delete a file from RunPlutus bucket
   */
  async deleteRunPlutusFile(
    folder: string,
    fileName: string
  ): Promise<DeleteResponse> {
    const url = `${this.baseUrl}/runplutus/${encodeURIComponent(folder)}/${encodeURIComponent(fileName)}`;
    const response = await httpService.delete<DeleteResponse>(url);
    return response.data;
  }

  /**
   * Download a file from RunPlutus bucket as Blob
   */
  async downloadRunPlutusFile(folder: string, fileName: string): Promise<Blob> {
    const url = this.getRunPlutusFileUrl(folder, fileName);
    return this.fetchAsBlob(url);
  }

  /**
   * Get the API URL for a RunPlutus file (for direct fetching or preview)
   */
  getRunPlutusFileUrl(folder: string, fileName: string): string {
    const path = `${this.baseUrl}/runplutus/${encodeURIComponent(folder)}/${encodeURIComponent(fileName)}`;
    return `${configService.getApiBaseUrl()}${path}`;
  }

  // ============================================
  // Helper Methods
  // ============================================

  /**
   * Check if a content type is previewable (image or PDF)
   */
  isPreviewable(contentType: string): boolean {
    return this.isImage(contentType) || this.isPdf(contentType);
  }

  /**
   * Check if a content type is an image
   */
  isImage(contentType: string): boolean {
    const normalized = contentType.toLowerCase();
    return IMAGE_TYPES.some((type) => normalized.startsWith(type));
  }

  /**
   * Check if a content type is a PDF
   */
  isPdf(contentType: string): boolean {
    return contentType.toLowerCase() === PDF_TYPE;
  }

  /**
   * Extract just the file name from a full path
   */
  extractFileName(fullPath: string): string {
    const parts = fullPath.split("/");
    return parts[parts.length - 1] || fullPath;
  }

  /**
   * Extract the folder portion from a full file path (everything except the filename)
   * For "uploads/reports/file.pdf", returns "uploads/reports"
   * For "uploads/file.pdf", returns "uploads"
   * For "file.pdf", returns ""
   */
  extractFileFolder(fullPath: string): string {
    const parts = fullPath.split("/");
    if (parts.length <= 1) {
      return "";
    }
    return parts.slice(0, -1).join("/");
  }

  /**
   * Parse FileInfo to include computed properties
   */
  parseFileInfo(file: FileInfo): ParsedFileInfo {
    return {
      ...file,
      displayName: this.extractFileName(file.name),
      fileSubfolder: this.extractFileFolder(file.name),
      isFolder: false,
      isPreviewable: this.isPreviewable(file.contentType),
      isImage: this.isImage(file.contentType),
      isPdf: this.isPdf(file.contentType),
      formattedSize: formatFileSize(file.size),
      formattedDate: new Date(file.updated).toLocaleDateString(),
    };
  }

  /**
   * Parse an array of FileInfo objects
   */
  parseFileInfoList(files: FileInfo[]): ParsedFileInfo[] {
    return files.map((file) => this.parseFileInfo(file));
  }

  /**
   * Process files to include virtual folder entries for nested paths.
   * Given files at various nesting levels, this extracts immediate subfolders
   * at the current level and returns them as folder entries before actual files.
   * 
   * @param files - Raw files from API
   * @param basePath - Current base path (e.g., "123" for provider root, "123/documents" for subfolder)
   * @param stripPrefix - Optional prefix to strip from folder paths for API calls (e.g., provider ID "123")
   * @returns Combined list of folder entries and file entries at current level
   */
  parseFilesWithFolders(files: FileInfo[], basePath: string, stripPrefix?: string): ParsedFileInfo[] {
    const basePrefix = basePath ? `${basePath}/` : "";
    const folders = new Map<string, ParsedFileInfo>();
    const directFiles: ParsedFileInfo[] = [];

    // Compute fileSubfolder: strip prefix from basePath for API compatibility
    // For providers: basePath="123/documents", stripPrefix="123" -> fileSubfolder="documents"
    // For RunPlutus: basePath="uploads/reports", no stripPrefix -> fileSubfolder="uploads/reports"
    const computeFileSubfolder = (path: string): string => {
      if (!path) return "";
      if (stripPrefix) {
        if (path === stripPrefix) return "";
        if (path.startsWith(`${stripPrefix}/`)) {
          return path.slice(stripPrefix.length + 1);
        }
      }
      return path;
    };

    const currentFileSubfolder = computeFileSubfolder(basePath);

    for (const file of files) {
      // Get path relative to base
      let relativePath = file.name;
      if (basePrefix && relativePath.startsWith(basePrefix)) {
        relativePath = relativePath.slice(basePrefix.length);
      }

      const segments = relativePath.split("/");

      if (segments.length === 1) {
        // Direct file at current level - build parsed file with correct fileSubfolder
        directFiles.push({
          ...file,
          displayName: this.extractFileName(file.name),
          fileSubfolder: currentFileSubfolder,
          isFolder: false,
          isPreviewable: this.isPreviewable(file.contentType),
          isImage: this.isImage(file.contentType),
          isPdf: this.isPdf(file.contentType),
          formattedSize: formatFileSize(file.size),
          formattedDate: new Date(file.updated).toLocaleDateString(),
        });
      } else {
        // Nested file - extract immediate subfolder
        const folderName = segments[0];
        if (!folders.has(folderName)) {
          // Create virtual folder entry
          folders.set(folderName, {
            name: basePath ? `${basePath}/${folderName}` : folderName,
            size: 0,
            contentType: "folder",
            updated: file.updated, // Use most recent file's date
            displayName: folderName,
            fileSubfolder: currentFileSubfolder,
            isFolder: true,
            isPreviewable: false,
            isImage: false,
            isPdf: false,
            formattedSize: "-",
            formattedDate: new Date(file.updated).toLocaleDateString(),
          });
        } else {
          // Update folder date to most recent file
          const existing = folders.get(folderName)!;
          if (new Date(file.updated) > new Date(existing.updated)) {
            existing.updated = file.updated;
            existing.formattedDate = new Date(file.updated).toLocaleDateString();
          }
        }
      }
    }

    // Return folders first, sorted alphabetically, then files
    const sortedFolders = Array.from(folders.values()).sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
    const sortedFiles = directFiles.sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );

    return [...sortedFolders, ...sortedFiles];
  }

  /**
   * Get file type icon name based on content type
   */
  getFileIconType(
    contentType: string
  ): "image" | "pdf" | "text" | "code" | "file" {
    if (this.isImage(contentType)) return "image";
    if (this.isPdf(contentType)) return "pdf";
    if (contentType.startsWith("text/")) return "text";
    if (
      contentType.includes("javascript") ||
      contentType.includes("json") ||
      contentType.includes("xml")
    ) {
      return "code";
    }
    return "file";
  }

  // ============================================
  // Private Methods
  // ============================================

  /**
   * Fetch a file as a Blob using the centralized httpService
   * This ensures consistent auth, headers, and error handling
   */
  private async fetchAsBlob(url: string): Promise<Blob> {
    return httpService.downloadBlob(url);
  }
}

export const blobService = new BlobService();
export default blobService;

